<template>
  <div
    class="package-table"
    :style="{ marginTop: presetKitId ? '25px' : '5px' }"
  >
    <CustomTable
      :title="tableTitle"
      :rowsData="data"
      :columns="projectId ? projectMaterialsColumns : packageMaterialsColumns"
      :loading="isFetching"
      :custom-body="true"
      :small="true"
    >
      <template #customBody="props">
        <q-tr>
          <custom-table-cell align="center" vertical-align="center">
            {{ props.row.material_id }}
          </custom-table-cell>
          <custom-table-cell align="left" vertical-align="center">
            {{ props.row.material_name }}
          </custom-table-cell>
          <template v-if="projectId">
            <custom-table-cell align="right" vertical-align="center">
              <span>{{ props.row.material_count }}</span>
            </custom-table-cell>
            <custom-table-cell align="left" vertical-align="center">
              <span>{{ props.row.consume_unit_name }}</span>
            </custom-table-cell>
            <custom-table-cell align="right" vertical-align="center">
              <Amount :value="props.row.material_price" />
            </custom-table-cell>
            <custom-table-cell align="right" vertical-align="center">
              <Amount :value="props.row.material_amount_sum" />
            </custom-table-cell>
          </template>
          <custom-table-cell
            align="center"
            vertical-align="center"
            style="width: 200px; min-width: 200px;"
          >
            <div
              style="display: flex; justify-content: center; align-items: center; height: 32px;"
            >
              <span
                v-if="editingRowId !== props.row.material_id"
                >{{ props.row.purchase_name }}</span
              >
              <div v-else style="max-width: 170px;">
                <CustomSelect
                  optionValueKey="purchase_type_id"
                  optionLabelKey="purchase_name"
                  :options="purchaseTypes || []"
                  :value="editingData.purchase_type_id"
                  @change="handlePurchaseChange"
                  small
                  :clearable="false"
                  :fullWidth="true"
                />
              </div>
            </div>
          </custom-table-cell>
          <custom-table-cell
            align="center"
            vertical-align="center"
            style="width: 100px; min-width: 100px;"
          >
            <div
              style="display: flex; justify-content: center; align-items: center; height: 32px;"
            >
              <CustomIcon
                v-if="editingRowId !== props.row.material_id"
                icon="edit"
                title="Редактировать"
                @click="handleEditClick(props.row)"
              />
              <div v-else style="display: flex; gap: 8px; align-items: center;">
                <CustomButton
                  color="primary-light"
                  :loading="isSaving"
                  @click="handleSaveEdit(props.row)"
                  style="width: 32px; height: 32px; min-height: 32px; padding: 0;"
                >
                  <q-icon name="check" size="16px" />
                </CustomButton>
                <CustomButton
                  color="negative"
                  @click="handleCancelEdit"
                  style="width: 32px; height: 32px; min-height: 32px; padding: 0;"
                >
                  <q-icon name="close" size="16px" />
                </CustomButton>
              </div>
            </div>
          </custom-table-cell>
        </q-tr>
      </template>
    </CustomTable>
  </div>
</template>

<script setup>
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import Amount from "@/components/common/Amount.vue";
import CustomButton from "@/components/common/CustomButton.vue";
import CustomIcon from '@/components/common/CustomIcon';
import CustomTable from '@/components/common/CustomTable';
import CustomTableCell from '@/components/common/CustomTable/custom-table-cell';
import CustomSelect from '@/components/form/CustomSelect';
import { useQuery } from '@tanstack/vue-query';
import { computed, ref } from 'vue';
import { getProjectSmeta, updateProjectMaterial } from "../ProjectsEntry/services";
import {
  getPackageMaterials,
  getPackagePurchaseTypes,
  packageMaterialsColumns,
  projectMaterialsColumns,
  updPackageMaterial
} from './services';

const props = defineProps({
  presetKitId: {
    type: Number,
    required: false
  },
  title: {
    type: String,
    default: ''
  },
  projectId: {
    type: Number,
  },
})
const { showSucceedSnackbar } = useCustomSnackbar();

const isFetching = ref(false)
const data = ref([])
const editingRowId = ref(null)
const editingData = ref({})
const isSaving = ref(false)

const tableTitle = computed(() => props.title)

const getData = async () => {
  isFetching.value = true
  const res = props.projectId ? await getProjectSmeta(props.projectId) : await getPackageMaterials(props.presetKitId)
  isFetching.value = false
  data.value = res || []
}

useQuery({
  queryKey: ['materials', props.projectId || props.presetKitId],
  queryFn: () => getData(),
  enabled: computed(() => !!props.projectId || !!props.presetKitId)
})

const { data: purchaseTypes } = useQuery({
  queryKey: ['purchaseTypes'],
  queryFn: getPackagePurchaseTypes,
  initialData: [],
})

function handleEditClick(row) {
  editingRowId.value = row.material_id
  editingData.value = {
    purchase_type_id: row.purchase_type_id
  }
}

function handleCancelEdit() {
  editingRowId.value = null
  editingData.value = {}
}

async function handleSaveEdit(row) {
  const body = {
    [props.projectId ? 'project_id' : 'preset_kit_id']: props.projectId || props.presetKitId,
    material_id: row.material_id,
    purchase_type_id: editingData.value.purchase_type_id
  }
  isSaving.value = true
    const res = props.projectId ? await updateProjectMaterial(body) : await updPackageMaterial(body)
  isSaving.value = false
  if(!res) return
  showSucceedSnackbar()
  data.value = res
  editingRowId.value = null
  editingData.value = {}
}

function handlePurchaseChange(value) {
  editingData.value.purchase_type_id = value
}
</script>

<style lang="scss" scoped>
.package-table {
  margin-top: 25px;
  .table-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  @media (max-width: 768px) {
    .row {
      margin-right: 16px;
    }
  }
  .createBtn {
    width: auto;
    font-weight: 400;
    height: 44px;
  }
}
</style>
